<template>
   <div :id="id" class="chart-gallery-wrapper" :class="{ 'white-bg': isLight }">
      <h2 class="header text-center" v-text="config.header" />
      <p class="sub-heading" v-text="config.longDescription" />
      <div class="chart-section-wrapper">
         <div class="smaller-screens">
            <BaseDropdown v-model="storeId" placeholder="Select your Chart" :options="getCharts" />
         </div>
         <div class="larger-screens" style="position: relative">
            <!-- <ul id="check" class="chart-names-section" @scroll="gradient"> -->
            <ul id="check" class="chart-names-section">
               <li
                  v-for="(item, index) in getCharts"
                  :key="index"
                  :class="{ active: index === selectedTab }"
                  @click="changeSelectedTab(index)"
               >
                  <i :class="['icon', item.icon]" />
                  <span class="name" v-text="item.text" />
                  <i
                     v-if="isVisibleOnViewport"
                     :class="[
                        'fc_indicator_right ml-auto',
                        { 'active-arrow': index === selectedTab },
                     ]"
                  ></i>
               </li>
            </ul>
         </div>

         <div class="chart-stage-col">
            <div class="charts-container">
               <div :id="containerId" ref="chartMainCon" class="show-chart"></div>
            </div>
            <div class="themes">
               <ChartTheme :tab-index="storeId" :container-id="containerId" :lang-type="langType" />
            </div>
         </div>
      </div>
      <div class="btn-container mt-5">
         <b-link
            v-for="(cta, index) in config.ctas"
            v-if="!langType"
            :id="'exploreChartAndMaps' + index"
            :key="index"
            :to="cta.link"
            :class="[
               'btn text-uppercase',
               index === 0 ? 'btn-primary-grad' : 'btn-outline-primary',
            ]"
            @click="btnAction"
            v-text="cta.header"
         />
      </div>
   </div>
</template>

<script>
import BaseDropdown from '../core/BaseDropdown.vue';
import ChartTheme from './ChartTheme.vue';
import $service from '../../util/service';
import code from '../../util/code.js';

export default {
   components: {
      BaseDropdown,
      ChartTheme,
   },

   props: {
      config: {
         type: Object,
         default: () => ({}),
      },

      isLight: {
         type: Boolean,
         default: false,
      },

      id: {
         type: String,
         default: 'chartShowCase',
      },
      langType: {
         type: Object,
         default: () => {},
      },
   },

   data: () => {
      const containerId = `chart-container-${Date.now()}`;
      return {
         selectedTab: 0,
         storeId: '',
         containerId,
         isVisibleOnViewport: false,
      };
   },

   computed: {
      getCharts() {
         return this.config.charts;
      },
   },

   watch: {
      storeId() {
         this.isVisibleOnViewport && this.renderChart();
      },
   },

   mounted() {
      window.addEventListener('scroll', () => {
         const element = document.querySelector(`#${this.id}`);
         if (this.isVisibleOnViewport) {
            return;
         }

         const position = element && element.getBoundingClientRect();

         // FULLY VISIBLE (position.top >= 0 && position.bottom <= window.innerHeight)
         // checking for partial visibility
         if (element && position.top < window.innerHeight && position.bottom >= 0) {
            this.isVisibleOnViewport = true;
            this.storeId = this.getCharts[0].value;
            this.renderChart();
         }
      });
   },

   methods: {
      gradient() {
         const elem = document.getElementById('check');
         const scrollableDiv = elem.scrollTop;
         const elemH = elem.getBoundingClientRect().height;
         const h = elem.children[1].offsetHeight * (elem.children.length + 0.5) - elemH;

         if (scrollableDiv > 10) {
            elem.parentNode.classList.add('topgradient');
         } else {
            elem.parentNode.classList.remove('topgradient');
         }

         if (scrollableDiv > h) {
            elem.parentNode.classList.remove('bottomgradient');
         } else {
            elem.parentNode.classList.add('bottomgradient');
         }
      },

      changeSelectedTab(index) {
         this.selectedTab = index;
         this.storeId = this.getCharts[index].value;
      },

      async renderChart() {
         const theme = 'candy';
         const fetchData = await $service.get(`/explore/generate/${this.storeId}/js/false`);
         fetchData.data.jsCode = fetchData.data.jsCode.replace(
            /"theme":.*"/g,
            `"theme": "${theme}"`
         );
         const jscode = fetchData.data.jsCode.replace(' chart: {', `chart: {\n "theme": "candy",`);

         let jsString = code.fixChartWidth(this.$refs.chartMainCon, jscode, {
            heightOperation: true,
         });
         // fixChartWidth only shrinks a chart that's bigger than its
         // wrapper - it never grows one that's smaller. These sample
         // configs were sized for the old, narrower/taller stage card, so
         // most now render undersized in the new wider/shorter one,
         // floating in visible empty space instead of filling it. Force
         // both dimensions to the wrapper's actual size instead.
         jsString = this.forceChartSize(this.$refs.chartMainCon, jsString);

         try {
            jsString = jsString.replace('chart-container', this.containerId);
            this.$executeScripts(jsString);
         } catch (error) {
            throw new Error(`Landing - Code executions failed ${jsString}`);
         }
      },

      forceChartSize(wrapperEl, jsCode) {
         const wrapperStyles = window.getComputedStyle(wrapperEl);
         const contentWidth = Math.round(
            parseFloat(wrapperStyles.width) -
               parseFloat(wrapperStyles.paddingLeft) -
               parseFloat(wrapperStyles.paddingRight)
         );
         const contentHeight = Math.round(
            parseFloat(wrapperStyles.height) -
               parseFloat(wrapperStyles.paddingTop) -
               parseFloat(wrapperStyles.paddingBottom)
         );

         return jsCode
            .replace(
               /([{},]\s*\\?"?'?width\\?"?'?:\s*\\?"?'?)([\w%]*)(\\?"?'?)/,
               `$1${contentWidth}$3`
            )
            .replace(
               /([{},]\s*\\?"?'?height\\?"?'?:\s*\\?"?'?)([\w%]*)(\\?"?'?)/,
               `$1${contentHeight}$3`
            );
      },

      btnAction() {
         // metric tracker
         this.$tracker.common.clickedOn('Explore All Charts and Maps');
      },
   },
};
</script>

<style lang="scss" scoped>
$backgroundColor: #f6f7fb;
$whiteColor: #ffffff;
.chart-gallery-wrapper {
   padding: 60px 0 70px 0;
   background-color: $backgroundColor;
   border-top: 1px solid #d8dce6;
   border-bottom: 1px solid #d8dce6;
   &.white-bg {
      background-color: $whiteColor;
      border-top: 0;
      border-bottom: 0;
   }

   h2.header {
      font-size: 32px;
      letter-spacing: 0.5px;
      line-height: 42px;
      text-align: center;
      width: 95%;
      margin: 0 auto 10px;
   }

   .h2 {
      font-size: 32px;
      letter-spacing: 0.5px;
      line-height: 42px;
      text-align: center;
      width: 95%;
      margin: 0 auto 10px auto;
   }
   p.sub-heading {
      font-size: 16px;
      color: #686980;
      width: 920px;
      line-height: 24px;
      margin: 0 auto 50px auto;
      text-align: center;
      @media screen and (max-width: 950px) {
         width: 90%;
      }
      @media screen and (max-width: 1279px) {
         margin: 0 auto 30px auto;
      }
   }
   .chart-section-wrapper {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      max-width: 1200px;
      // The reference's own .wrap class (which this section's container is
      // meant to match) carries this same inset - without it the stage
      // column ends up ~48px wider than intended, on top of already being
      // wider than the old fixed-width card since it now fills the grid's
      // 1fr column instead.
      padding: 0 24px;
      margin: 44px auto 0;
      box-sizing: border-box;
      @media screen and (max-width: 1279px) {
         flex-direction: column;
         align-items: center;
      }
      @media screen and (min-width: 1280px) {
         display: grid;
         grid-template-columns: 270px 1fr;
         gap: 28px;
         // stretch (not start): the picker list's height varies with how
         // many chart types are in it, and the stage card should match
         // that height rather than sitting at a fixed size next to
         // whatever empty space the list leaves below it.
         align-items: stretch;
      }
      .smaller-screens {
         @media screen and (min-width: 1280px) {
            display: none;
         }
         .base-dropdown {
            margin-bottom: 50px;
         }
      }
      .larger-screens {
         @media screen and (max-width: 1279px) {
            display: none;
         }
      }
      .chart-names-section {
         border: 1px solid #e6e8ef;
         border-radius: 13px;
         overflow: hidden;
         background: #ffffff;
         padding-left: 0;
         margin: 0;

         li {
            list-style-type: none;
            padding: 13px 15px;
            margin: 0;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 11px;
            color: #3c415c;
            transition: background 0.13s ease, color 0.13s ease;

            & + li {
               border-top: 1px solid #e6e8ef;
            }
            &:hover {
               background: #f6f7fb;
            }

            .name {
               font-size: 14px;
               font-weight: 600;
               line-height: 21px;
            }
            i {
               &.icon {
                  height: 40px;
                  width: 40px;
                  flex-shrink: 0;
                  display: inline-block;
                  opacity: 0.85;
               }

               &.fc_indicator_right {
                  font-size: 1.1rem;
                  line-height: 1;
                  margin-left: auto;
                  opacity: 0;
                  transition: opacity 0.13s ease;
               }
            }
            &.active {
               background: #5b2fb0;
               color: $whiteColor;
               pointer-events: none;

               i.icon {
                  opacity: 1;
               }
               i.fc_indicator_right {
                  opacity: 1;
               }
            }
         }
      }
      .chart-stage-col {
         min-width: 0;
         width: 100%;
         display: flex;
         flex-direction: column;
      }
      .charts-container {
         background-color: #1b1f2b;
         border: 1px solid rgba(11, 11, 40, 0.06);
         border-radius: 13px;
         padding: 8px 8px 0;
         box-shadow: 0 18px 40px -16px rgba(11, 11, 40, 0.22), 0 2px 6px rgba(11, 11, 40, 0.05);
         // Grows to fill whatever height align-items:stretch gives
         // .chart-stage-col (matching the picker list's height), with
         // .themes taking its own natural height below it.
         display: flex;
         flex-direction: column;
         flex: 1;
         min-height: 330px;

         .show-chart {
            // Fills the now dynamically-sized card above, rather than a
            // fixed value - fixChartWidth()/forceChartSize() read this
            // element's actual computed height at render time, so the
            // chart itself always matches whatever height that ends up
            // being instead of a magic-number match to just this specific
            // picker list's length.
            flex: 1;
            text-align: center;
            display: flex;
            align-items: center;
            justify-content: space-around;
            padding: 20px;

            // FusionCharts' SVG renderer always paints a literal white
            // background-color on the <svg> node itself, hidden only by a
            // <rect> it draws inside once the chart's data/theme is ready.
            // Switching charts tears down and recreates that <svg> from
            // scratch, so for a frame or two the raw white background is
            // exposed before the new chart paints over it - force it
            // transparent so the dark stage shows through the whole time.
            ::v-deep svg {
               background-color: transparent !important;
            }
         }
      }
      .themes {
         margin-top: 16px;
      }
   }
   // .btn {
   //    display: block;
   //    margin: 55px auto 0 auto;
   //    width: 245px;
   // }

   .btn-container {
      text-align: center;
      .btn {
         margin: 5px;
      }

      @media only screen and (max-width: 425px) {
         .btn {
            display: block;
            max-width: 245px;
            margin: 5px auto;
         }
      }
   }
}

.larger-screens {
   position: relative;
   transition: all ease 1s;
   &.bottomgradient {
      &:after {
         content: '';
         display: block;
         width: 100%;
         height: 65px;
         clear: both;
         position: absolute;
         bottom: 0;
         left: -12px;
         background-image: linear-gradient(0deg, #f9f9fb 33%, rgba(249, 249, 251, 0) 100%);
         transition: all ease 1s;
      }
   }

   &.topgradient {
      &:before {
         content: '';
         display: block;
         width: 100%;
         height: 30px;
         clear: both;
         position: absolute;
         z-index: 2;
         top: 0;
         left: -12px;
         background-image: linear-gradient(
            0deg,
            rgba(249, 249, 251, 0) 0%,
            rgba(249, 249, 251, 1) 100%
         );
         transition: all ease 1s;
      }
   }
}

.white-bg {
   .larger-screens {
      &.bottomgradient {
         &:after {
            background-image: linear-gradient(
               0deg,
               #ffffff 33%,
               rgba(255, 255, 255, 0) 100%
            ) !important;
         }
      }

      &.topgradient {
         &:before {
            background-image: linear-gradient(
               0deg,
               rgba(255, 255, 255, 0) 0%,
               rgb(255, 255, 255) 100%
            ) !important;
         }
      }
   }
}

// Icons from directory ""

%fc-showcase {
   background-repeat: no-repeat;
   width: 40px;
   height: 40px;
}

.chart-names-section {
   .icon {
      // background-image: url('/public/images/icons-showcase.png');
      // background-image: url('/public/images/icons-showcase.svg');
      &.icon_fc_gauges,
      &.icon_fc_gauges\:regular {
         @extend %fc-showcase;
         background-position: 0 0;
      }

      &.icon_fc_boxwhisker,
      &.icon_fc_boxwhisker\:regular {
         @extend %fc-showcase;
         background-position: 0 -40px;
      }

      &.icon_fc_bubble,
      &.icon_fc_bubble\:regular {
         @extend %fc-showcase;
         background-position: 0 -80px;
      }

      &.icon_fc_column_chart,
      &.icon_fc_column_chart\:regular {
         @extend %fc-showcase;
         background-position: 0 -120px;
      }

      &.icon_fc_donut,
      &.icon_fc_donut\:regular {
         @extend %fc-showcase;
         background-position: 0 -160px;
      }

      &.icon_fc_funnel,
      &.icon_fc_funnel\:regular {
         @extend %fc-showcase;
         background-position: 0 -200px;
      }

      &.icon_fc_gantt,
      &.icon_fc_gantt\:regular {
         @extend %fc-showcase;
         background-position: 0 -240px;
      }

      &.icon_fc_multi_axis,
      &.icon_fc_multi_axis\:regular {
         @extend %fc-showcase;
         background-position: 0 -280px;
      }

      &.icon_fc_multivariate,
      &.icon_fc_multivariate\:regular {
         @extend %fc-showcase;
         background-position: 0 -320px;
      }

      &.icon_fc_pie_chart,
      &.icon_fc_pie_chart\:regular {
         @extend %fc-showcase;
         background-position: 0 -360px;
      }

      &.icon_fc_radar,
      &.icon_fc_radar\:regular {
         @extend %fc-showcase;
         background-position: 0 -400px;
      }

      &.icon_fc_candlestick,
      &.icon_fc_candlestick\:regular {
         @extend %fc-showcase;
         background-position: 0 -440px;
      }

      &.icon_fc_column_chart_timeseries,
      &.icon_fc_column_chart_timeseries\:regular {
         @extend %fc-showcase;
         background-position: 0 -480px;
      }

      &.icon_fc_line_chart_annoted_timeseries,
      &.icon_fc_line_chart_annoted_timeseries\:regular {
         @extend %fc-showcase;
         background-position: 0 -520px;
      }

      &.icon_fc_line_chart_timeseries,
      &.icon_fc_line_chart_timeseries\:regular {
         @extend %fc-showcase;
         background-position: 0 -560px;
      }

      &.icon_fc_treemap,
      &.icon_fc_treemap\:regular {
         @extend %fc-showcase;
         background-position: 0 -600px;
      }

      &.icon_fc_usmaps,
      &.icon_fc_usmaps\:regular {
         @extend %fc-showcase;
         background-position: 0 -640px;
      }

      &.icon_fc_world,
      &.icon_fc_world\:regular {
         @extend %fc-showcase;
         background-position: 0 -680px;
      }
   }
}
</style>
